// キービジュアル：縦長動画のスライダー（sections/kv-video-slider.liquid）
// 中央（スマホは表示中）の動画だけ再生し、再生し終わったら次のスライドへ進む。
if (!customElements.get('kv-video-slider')) {
  customElements.define(
    'kv-video-slider',
    class KvVideoSlider extends HTMLElement {
      connectedCallback() {
        this.slides = Array.from(this.querySelectorAll('.kv-video-slider__slide'));
        this.dots = Array.from(this.querySelectorAll('.kv-video-slider__dot'));
        if (this.slides.length === 0) return;

        // 登録枚数（インジケーターはこの枚数で数える）
        this.realCount = this.slides.length;
        this.cloneSlides();

        this.current = 0;
        this.offsets = this.slides.map(() => 0);
        this.fallbackDuration = (Number(this.dataset.fallbackDuration) || 6) * 1000;
        this.isVisible = true;

        this.slides.forEach((slide) => {
          const video = slide.querySelector('video');
          if (!video) return;
          video.muted = true;
          video.playsInline = true;
          video.addEventListener('ended', () => {
            if (slide.classList.contains('is-active')) this.next();
          });
          // 再生できない動画は一定時間で次へ
          video.addEventListener('error', () => {
            if (slide.classList.contains('is-active')) this.scheduleFallback();
          });
        });

        this.querySelector('.kv-video-slider__arrow--prev')?.addEventListener('click', () => this.prev());
        this.querySelector('.kv-video-slider__arrow--next')?.addEventListener('click', () => this.next());
        this.dots.forEach((dot) => dot.addEventListener('click', () => this.goToReal(Number(dot.dataset.index))));

        // PC：左右のスライドをクリックするとそのスライドへ
        this.slides.forEach((slide, index) =>
          slide.addEventListener('click', (event) => {
            if (slide.classList.contains('is-active')) return;
            event.preventDefault();
            this.navigate(index);
          })
        );

        this.initSwipe();
        this.initVisibility();
        this.initTopOffset();
        this.render(false);
        // 最初のフェードインは「中央 → 左 → 右」の順（render で位置が決まってから予約する）
        this.revealStartedAt = performance.now();
        this.slides.forEach((slide, index) => this.fadeInWhenLoaded(slide, index));
        this.playCurrent();
        this.updateProgress();
      }

      // PCは「左・中央・右」の3枠＋左右に待機枠（2枚ずつ）が要るので、7枚未満なら複製して補う。
      // 反対側へ回り込むスライドが必ず画面外にいるので、素早く送っても端にすき間が出ない。
      cloneSlides() {
        if (this.realCount < 2) return;
        const viewport = this.querySelector('.kv-video-slider__viewport');
        const originals = this.slides.slice();
        while (this.slides.length < 7) {
          originals.forEach((original) => {
            const clone = original.cloneNode(true);
            clone.classList.remove('is-active');
            clone.dataset.clone = 'true';
            Array.from(clone.attributes).forEach(({ name }) => {
              if (name.startsWith('data-shopify-editor')) clone.removeAttribute(name);
            });
            clone.querySelectorAll('video').forEach((video) => video.setAttribute('muted', ''));
            viewport.appendChild(clone);
            this.slides.push(clone);
          });
        }
      }

      // 動画は最初のコマ（またはサムネイル）、画像は読み込み完了でフェードイン
      fadeInWhenLoaded(slide, index) {
        const media = slide.querySelector('.kv-video-slider__media');
        const video = media?.querySelector('video');
        const image = media?.querySelector('img');
        if (!media) return;
        // 動画も画像もない（仮の画像）スライドは、すぐ表示済みにする
        if (!video && !image) return media.classList.add('is-loaded');

        let done = false;
        const show = () => {
          if (done) return;
          done = true;
          // 中央 0.1秒 → 左 0.45秒 → 右 0.8秒 の順に出す（読み込みが遅れたら読み込めた時点で）。画面外は待たない
          const revealAt = { 0: 100, '-1': 450, 1: 800 }[this.offsets[index]] ?? 0;
          const delay = Math.max(revealAt - (performance.now() - this.revealStartedAt), 0);
          setTimeout(() => media.classList.add('is-loaded'), delay);
        };

        if (video) {
          if (video.readyState >= 2) return show();
          video.addEventListener('loadeddata', show, { once: true });
          video.addEventListener('playing', show, { once: true });
          // サムネイル（poster）が先に読み込めたら、それで表示する
          if (video.poster) {
            const poster = new Image();
            poster.onload = show;
            poster.src = video.poster;
          }
        } else {
          if (image.complete && image.naturalWidth) return show();
          image.addEventListener('load', show, { once: true });
        }
        // 読み込みが極端に遅い・失敗したときも、いつまでも隠れたままにしない
        setTimeout(show, 5000);
      }

      realIndex(index = this.current) {
        return index % this.realCount;
      }

      // インジケーターから：その番号のスライドのうち、いちばん近いものへ移動
      goToReal(realIndex) {
        let delta = (((realIndex - this.realIndex()) % this.realCount) + this.realCount) % this.realCount;
        if (delta > this.realCount / 2) delta -= this.realCount;
        this.navigate(this.current + delta);
      }

      // 「画面の高さに合わせる」用：キービジュアルより上（お知らせバー＋ヘッダー）の高さを CSS 変数に入れる。
      // ページ上端からの距離はスクロールやヘッダーの出入りでは変わらないので、上の要素の大きさが変わった時だけ計算し直す。
      initTopOffset() {
        if (!this.classList.contains('kv-video-slider--fit-viewport')) return;
        const update = () => {
          const top = Math.round(this.getBoundingClientRect().top + window.scrollY);
          this.style.setProperty('--kv-top-offset', `${Math.max(top, 0)}px`);
        };
        update();
        document.fonts?.ready.then(update);
        if ('ResizeObserver' in window) {
          this.topObserver = new ResizeObserver(update);
          document.querySelectorAll('.shopify-section-group-header-group').forEach((el) => this.topObserver.observe(el));
        }
        // 横幅が変わった時だけ再計算（アドレスバーの開閉による高さだけの変化では再計算しない）
        let lastWidth = window.innerWidth;
        window.addEventListener('resize', () => {
          if (window.innerWidth === lastWidth) return;
          lastWidth = window.innerWidth;
          update();
        });
      }

      disconnectedCallback() {
        this.topObserver?.disconnect();
        clearTimeout(this.fallbackTimer);
        cancelAnimationFrame(this.progressFrame);
        this.observer?.disconnect();
      }

      // インジケーター：表示中スライドの進み具合（0〜1）をバーに反映
      updateProgress() {
        const dot = this.dots[this.realIndex()];
        if (dot) {
          const video = this.slides[this.current].querySelector('video');
          let progress = 0;
          if (video && video.duration && !this.fallbackStartedAt) {
            progress = video.currentTime / video.duration;
          } else if (this.fallbackStartedAt) {
            progress = (performance.now() - this.fallbackStartedAt) / this.fallbackDuration;
          }
          dot.style.setProperty('--kv-progress', Math.min(Math.max(progress, 0), 1));
        }
        this.progressFrame = requestAnimationFrame(() => this.updateProgress());
      }

      next() {
        this.navigate(this.current + 1);
      }

      prev() {
        this.navigate(this.current - 1);
      }

      // 手動・自動の送りは最短 0.45 秒間隔にする（連打でスライドが追いつかず端が空くのを防ぐ）
      navigate(index) {
        const now = performance.now();
        if (this.lastNavigatedAt && now - this.lastNavigatedAt < 450) return;
        this.lastNavigatedAt = now;
        this.goTo(index);
      }

      goTo(index) {
        const count = this.slides.length;
        this.current = ((index % count) + count) % count;
        this.render(true);
        this.playCurrent();
      }

      // 各スライドの位置（中央からのずれ）を計算して並べる
      render(animate) {
        const count = this.slides.length;
        this.slides.forEach((slide, index) => {
          let offset = (((index - this.current) % count) + count) % count;
          if (offset > count / 2) offset -= count;

          const previous = this.offsets[index];
          const jump = !animate || Math.abs(offset - previous) > 1;
          slide.classList.toggle('is-jumping', jump);
          slide.style.setProperty('--kv-offset', offset);
          slide.classList.toggle('is-active', offset === 0);
          // 左の表示枠（-1）と、そこから画面外へ出ていく途中の枠（-2）
          slide.classList.toggle('is-prev', offset === -1 || offset === -2);
          slide.classList.toggle('is-next', offset === 1);
          slide.setAttribute('aria-hidden', offset === 0 ? 'false' : 'true');
          this.offsets[index] = offset;
        });

        // is-jumping（トランジションなし）を次のフレームで解除
        requestAnimationFrame(() => {
          requestAnimationFrame(() => this.slides.forEach((slide) => slide.classList.remove('is-jumping')));
        });

        const activeDot = this.realIndex();
        this.dots.forEach((dot, index) => {
          dot.classList.toggle('is-active', index === activeDot);
          if (index !== activeDot) dot.style.setProperty('--kv-progress', 0);
        });
      }

      // 動画の読み込み元を入れる（必要になった時だけ）。スマホは HLS（回線・画面に合わせて画質が変わる）、PC は MP4
      loadVideoSource(slide, preload = 'auto') {
        const video = slide?.querySelector('video');
        if (!video || video.dataset.sourceLoaded) return video;
        const { srcMp4, srcHls } = video.dataset;
        const isSmallTouch = window.matchMedia('(max-width: 749px), (pointer: coarse)').matches;
        const canPlayHls = srcHls && video.canPlayType('application/vnd.apple.mpegurl');
        video.preload = preload;
        video.src = isSmallTouch && canPlayHls ? srcHls : srcMp4 || srcHls;
        video.dataset.sourceLoaded = 'true';
        return video;
      }

      playCurrent() {
        clearTimeout(this.fallbackTimer);
        this.fallbackStartedAt = null;

        // 読み込むのは「再生する 1 本」と「次に再生する 1 本」だけ
        this.loadVideoSource(this.slides[this.current]);
        this.loadVideoSource(this.slides[(this.current + 1) % this.slides.length]);

        this.slides.forEach((slide, index) => {
          const video = slide.querySelector('video');
          if (!video) return;
          if (index === this.current) return;
          video.pause();
        });

        if (!this.isVisible) return;

        const video = this.slides[this.current].querySelector('video');
        if (!video) {
          if (this.slides.length > 1) this.scheduleFallback();
          return;
        }

        video.currentTime = 0;
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => this.scheduleFallback());
      }

      scheduleFallback(remaining = this.fallbackDuration) {
        clearTimeout(this.fallbackTimer);
        if (this.slides.length < 2) return;
        this.fallbackStartedAt = performance.now() - (this.fallbackDuration - remaining);
        this.fallbackTimer = setTimeout(() => this.next(), remaining);
      }

      // スマホ：左右スワイプで送る
      initSwipe() {
        const viewport = this.querySelector('.kv-video-slider__viewport');
        let startX = 0;
        let startY = 0;
        viewport.addEventListener(
          'touchstart',
          (event) => {
            startX = event.touches[0].clientX;
            startY = event.touches[0].clientY;
          },
          { passive: true }
        );
        viewport.addEventListener(
          'touchend',
          (event) => {
            const dx = event.changedTouches[0].clientX - startX;
            const dy = event.changedTouches[0].clientY - startY;
            if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy)) return;
            dx < 0 ? this.next() : this.prev();
          },
          { passive: true }
        );

        // PC：マウスのドラッグでも送る（ドラッグ後のクリックは無視）
        let mouseStartX = null;
        let dragged = false;
        viewport.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          mouseStartX = event.clientX;
          dragged = false;
        });
        window.addEventListener('pointerup', (event) => {
          if (mouseStartX === null) return;
          const dx = event.clientX - mouseStartX;
          mouseStartX = null;
          if (Math.abs(dx) < 40) return;
          dragged = true;
          dx < 0 ? this.next() : this.prev();
        });
        viewport.addEventListener(
          'click',
          (event) => {
            if (!dragged) return;
            dragged = false;
            event.preventDefault();
            event.stopPropagation();
          },
          true
        );
        viewport.addEventListener('dragstart', (event) => event.preventDefault());
      }

      // 画面外に出たら止めて、戻ったら再開
      initVisibility() {
        if (!('IntersectionObserver' in window)) return;
        this.observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            this.isVisible = entry.isIntersecting;
            const video = this.slides[this.current].querySelector('video');
            if (this.isVisible) {
              if (video && !this.fallbackStartedAt) {
                video.play().catch(() => this.scheduleFallback());
              } else if (this.fallbackRemaining != null) {
                this.scheduleFallback(this.fallbackRemaining);
              }
            } else {
              // 画面外では止める（画像スライドは残り時間を覚えておく）
              if (this.fallbackStartedAt) {
                this.fallbackRemaining = Math.max(
                  this.fallbackDuration - (performance.now() - this.fallbackStartedAt),
                  0
                );
                this.fallbackStartedAt = performance.now() - (this.fallbackDuration - this.fallbackRemaining);
              } else {
                this.fallbackRemaining = null;
              }
              clearTimeout(this.fallbackTimer);
              video?.pause();
            }
          });
        });
        this.observer.observe(this);
      }
    }
  );
}
